import BaseStockProvider from '../BaseStockProvider';

/**
 * Unsplash stock image provider
 * Free API: https://unsplash.com/developers
 * 
 * IMPORTANT: Unsplash API Guidelines for Production
 * To gain access to production-level rate limits (5,000 requests/hour):
 * 
 * 1. Hotlink photos - Photos must be hotlinked to the original image URL on Unsplash
 * 2. Trigger downloads - When a user uses a photo, trigger an event to the download endpoint
 * 3. Attribute photographer and Unsplash - Display "Photo by [Photographer Name] on Unsplash" with links
 * 4. Don't use 'Unsplash' in your app name or make it look similar to Unsplash
 * 
 * This implementation follows these guidelines.
 */
class UnsplashProvider extends BaseStockProvider {
  constructor(apiKey = null) {
    super({
      name: 'Unsplash',
      apiKey: apiKey || process.env.REACT_APP_UNSPLASH_API_KEY,
      baseUrl: 'https://api.unsplash.com',
      mediaTypes: ['image'],
      requiresAuth: true,
    });
  }

  /**
   * Search for images on Unsplash
   */
  async search({ query, page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Unsplash API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/search/photos?query=${encodeURIComponent(query)}&page=${page}&per_page=${perPage}`,
        {
          headers: {
            Authorization: `Client-ID ${this.apiKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Normalize and filter out invalid formats
      const normalizedItems = data.results.map(item => this.normalizeItem(item, 'image'));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: page < data.total_pages,
        total: data.total,
        page: page,
      };
    } catch (error) {
      console.error('Unsplash search error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Get popular/trending images
   */
  async getPopular({ page = 1, perPage = 20 }) {
    if (!this.apiKey) {
      console.warn('Unsplash API key not configured');
      return { data: [], hasMore: false, total: 0 };
    }

    try {
      const response = await fetch(
        `${this.baseUrl}/photos?page=${page}&per_page=${perPage}&order_by=popular`,
        {
          headers: {
            Authorization: `Client-ID ${this.apiKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Normalize and filter out invalid formats
      const normalizedItems = data.map(item => this.normalizeItem(item, 'image'));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: data.length === perPage,
        total: null, // Unsplash doesn't provide total for this endpoint
        page: page,
      };
    } catch (error) {
      console.error('Unsplash getPopular error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Trigger download event (REQUIRED by Unsplash API guidelines)
   * Must be called when user actually uses/downloads the photo
   * @param {string} downloadUrl - The download_location URL from the photo object
   */
  async triggerDownload(downloadUrl) {
    if (!this.apiKey || !downloadUrl) {
      return;
    }

    try {
      await fetch(downloadUrl, {
        headers: {
          Authorization: `Client-ID ${this.apiKey}`,
        },
      });
    } catch (error) {
      console.error('Unsplash download trigger error:', error);
    }
  }

  /**
   * Normalize Unsplash item to common format
   * Includes all necessary attribution data per Unsplash guidelines
   */
  normalizeItem(item, mediaType) {
    return {
      id: `unsplash_${item.id}`,
      provider: 'unsplash',
      name: item.description || item.alt_description || 'Untitled',
      thumbnail: item.urls.small, // Hotlinked to Unsplash (per guidelines)
      url: item.urls.regular, // Hotlinked to Unsplash (per guidelines)
      downloadUrl: item.urls.full, // Hotlinked to Unsplash (per guidelines)
      type: mediaType,
      category: 'Stock',
      description: item.description || item.alt_description,
      size: null, // Unsplash doesn't provide file size
      width: item.width,
      height: item.height,
      // Attribution data (REQUIRED by Unsplash guidelines)
      author: {
        name: item.user.name,
        username: item.user.username,
        profileUrl: item.user.links.html,
      },
      sourceUrl: item.links.html,
      // Download tracking URL (REQUIRED by Unsplash guidelines)
      downloadLocation: item.links.download_location,
      // Attribution text format: "Photo by [Name] on Unsplash"
      attribution: `Photo by ${item.user.name} on Unsplash`,
      attributionUrl: item.links.html,
      isUserUpload: false,
      color: item.color,
    };
  }

  /**
   * Get attribution text for display
   * Format: "Photo by [Photographer Name] on Unsplash"
   */
  getAttributionText(item) {
    return item.attribution || `Photo by ${item.author?.name || 'Unknown'} on Unsplash`;
  }
}

export default UnsplashProvider;